
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { useAuthActions } from "@contexts/auth";
import { useEffect, useState } from "react";
import { testConnection } from "./siengePermissionsApi";

const API_LIMIT_OPTIONS = ['100/10', '1000/20', '2500/50', '5000/100', '10000/200', '75000/28800'];

// A construtora sempre pertence a exatamente um ERP — essa escolha é a regra primária que
// direciona quais campos de credencial o formulário exige (mega_data_config, sienge_data_config
// ou prevision_token), por isso o campo ERP vem antes de qualquer campo de credencial.
const ERP_OPTIONS = ['MEGA', 'SIENGE', 'PREVISION'];

const emptyCompany = {
  name: '',
  subdomain: '',
  company_code: '',
  api_limits: API_LIMIT_OPTIONS[0],
  erp: '',
  tagsText: '',
  title_annulments_self_service: false,
}

// `tags` já foi visto como array de strings E como array de objetos (`{name}`/`{label}`/
// `{value}`) em documentos reais — sem isso, editar uma empresa com tags-objeto mostrava
// literalmente "[object Object]" no campo de texto.
const tagToText = (tag) => {
  if (typeof tag === 'string') return tag
  if (tag && typeof tag === 'object') return tag.name || tag.label || tag.value || JSON.stringify(tag)
  return String(tag ?? '')
}

const companyToFormState = (company) => ({
  name: company.name || '',
  subdomain: company.subdomain || '',
  company_code: company.company_code || '',
  api_limits: (company.api_limit_rest && company.api_limit_bulk)
    ? `${company.api_limit_rest}/${company.api_limit_bulk}`
    : API_LIMIT_OPTIONS[0],
  erp: company.erp || '',
  tagsText: (company.tags || []).map(tagToText).join(', '),
  title_annulments_self_service: (company.title_annulments_self_service ?? company.selfService) === true,
})

// Cada campo vira uma célula própria do grid — sem isso o fieldset (que também é grid, pra
// ficar no mesmo padrão visual do resto do formulário) intercalava label/input em colunas
// separadas em vez de empilhar label-sobre-input dentro da mesma célula.
const Field = ({ label, full, children }) => (
  <div style={full ? { gridColumn: '1 / -1' } : undefined}>
    <label>{label}</label>
    {children}
  </div>
)

// company: opcional — quando informado, o formulário abre em modo de edição (chama
// updateCompany em vez de createCompany). onSuccess: opcional — chamado após
// criação/edição (usado quando o formulário é aberto dentro de um Modal).
const NewCompany = ({ company, onSuccess }) => {
  const isEditing = !!company
  const [newCompany, setNewCompany] = useState(company ? companyToFormState(company) : emptyCompany)
  const [megaDataConfig, setMegaDataConfig] = useState(company?.mega_data_config || { url: '', user_login: '', user_pass: '' })
  const [siengeDataConfig, setSiengeDataConfig] = useState(company?.sienge_data_config || {
    subdomain: '', api_user: '', api_pass: '', api_limit_bulk: '', free: false,
  })
  const [previsionToken, setPrevisionToken] = useState(company?.prevision_token || '')
  const [deleteConfirming, setDeleteConfirming] = useState(false)
  const [actionFeedback, setActionFeedback] = useState('')
  const [connectionTest, setConnectionTest] = useState(null)
  const [testingConnection, setTestingConnection] = useState(false)
  const { createCompany, updateCompany, deleteCompany } = useProjectActions();

  // O teste de conexão valida a CREDENCIAL DE ERP, que é um atributo da construtora — não da
  // obra. Ficava no formulário de obra (onde só dava pra repetir o mesmo teste a cada obra da
  // mesma construtora, e bloqueava o cadastro por um problema que não era dele); o lugar certo
  // é aqui. A sonda precisa da credencial já persistida, então só existe em modo de edição.
  const handleTestConnection = async () => {
    if (!isEditing) return
    setTestingConnection(true)
    setConnectionTest(await testConnection(company._id))
    setTestingConnection(false)
  }

  // Grava em maiúsculas — mesmo padrão de `company_code` em `newBuilding.jsx`, pra ficar
  // idêntico ao que já foi persistido em `buildings.company_code` de obras existentes.
  const UPPERCASE_FIELDS = ['company_code']

  const handleInput = (e) => {
    const { name, type, checked, value } = e.target;
    const nextValue = UPPERCASE_FIELDS.includes(name) ? value.toUpperCase() : value
    setNewCompany(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : nextValue }))
  }

  const handleMegaInput = (e) => {
    const { name, value } = e.target;
    setMegaDataConfig(prev => ({ ...prev, [name]: value }))
  }

  const handleSiengeInput = (e) => {
    const { name, type, checked, value } = e.target;
    setSiengeDataConfig(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSend = async (e) => {
    e.preventDefault()
    // Rótulo do campo é "Api Limits (Rest/Bulk)": o primeiro número é Rest, o segundo é Bulk.
    // Mantidos como string — é assim que já estão persistidos em `companies` hoje (ex.:
    // api_limit_bulk: "10"), então não convertemos para number.
    const [rest, bulk] = newCompany.api_limits.split('/')
    const tags = newCompany.tagsText.split(',').map(t => t.trim()).filter(Boolean)
    const company_payload = {
      ...newCompany,
      api_limit_rest: rest,
      api_limit_bulk: bulk,
      tags,
      ...(newCompany.erp === 'MEGA' ? { mega_data_config: megaDataConfig } : {}),
      ...(newCompany.erp === 'SIENGE' ? { sienge_data_config: siengeDataConfig } : {}),
      ...(newCompany.erp === 'PREVISION' ? { prevision_token: previsionToken } : {}),
    }
    delete company_payload.api_limits
    delete company_payload.tagsText
    // Liga/desliga da autogestão só existe na edição (o padrão da construtora nova é desligado).
    if (!isEditing) delete company_payload.title_annulments_self_service

    const result = isEditing
      ? await updateCompany({ ...company_payload, company_id: company._id })
      : await createCompany(company_payload)

    if (result?.ok && !isEditing) {
      setNewCompany(emptyCompany)
      setMegaDataConfig({ url: '', user_login: '', user_pass: '' })
      setSiengeDataConfig({ subdomain: '', api_user: '', api_pass: '', api_limit_bulk: '', free: false })
      setPrevisionToken('')
    }
    onSuccess?.(result)
  }

  // (Des)ativar é reversível e some da listagem imediatamente — Remover é definitivo e só
  // funciona se a construtora não tiver obras vinculadas (checado no back-end).
  const toggleActive = async () => {
    const active = company.active === false
    const result = await updateCompany({ ...company, company_id: company._id, active })
    if (result?.ok) onSuccess?.(result)
    else setActionFeedback(result?.message || 'Não foi possível alterar o status.')
  }

  const confirmDelete = async () => {
    const result = await deleteCompany(company._id)
    if (result?.ok) {
      onSuccess?.(result)
    } else {
      setActionFeedback(result?.message || 'Não foi possível remover.')
      setDeleteConfirming(false)
    }
  }

  return (
    <>
      <div className="backoffice-form-card">
        <h2>{isEditing ? 'Editar Construtora' : 'Nova Construtora'}</h2>
        <form className="new-user-container new-company-container">
          <Field label="Nome">
            <input type="text" name='name' onChange={handleInput} value={newCompany.name} />
          </Field>
          <Field label="Subdomain">
            <input type="text" name='subdomain' onChange={handleInput} value={newCompany.subdomain} />
          </Field>
          <Field label="Código da Construtora">
            <input type="text" name='company_code' onChange={handleInput} value={newCompany.company_code} />
          </Field>
          <Field label="ERP">
            <select name="erp" onChange={handleInput} value={newCompany.erp}>
              <option disabled value=''>Choose</option>
              {ERP_OPTIONS.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </Field>

          {newCompany.erp === 'MEGA' && (
            <fieldset>
              <legend>Credenciais Mega</legend>
              <Field label="Url">
                <input type="text" name='url' onChange={handleMegaInput} value={megaDataConfig.url} />
              </Field>
              <Field label="User Login">
                <input type="text" name='user_login' onChange={handleMegaInput} value={megaDataConfig.user_login} />
              </Field>
              <Field label="User Pass">
                <input type="text" name='user_pass' onChange={handleMegaInput} value={megaDataConfig.user_pass} />
              </Field>
            </fieldset>
          )}

          {newCompany.erp === 'SIENGE' && (
            <fieldset>
              <legend>Credenciais Sienge</legend>
              <Field label="Subdomain">
                <input type="text" name='subdomain' onChange={handleSiengeInput} value={siengeDataConfig.subdomain} />
              </Field>
              <Field label="Api User">
                <input type="text" name='api_user' onChange={handleSiengeInput} value={siengeDataConfig.api_user} />
              </Field>
              <Field label="Api Pass">
                <input type="text" name='api_pass' onChange={handleSiengeInput} value={siengeDataConfig.api_pass} />
              </Field>
              <Field label="Api Limit Bulk">
                <input type="text" name='api_limit_bulk' onChange={handleSiengeInput} value={siengeDataConfig.api_limit_bulk} />
              </Field>
              <div className="checkbox-row">
                <label>
                  <input type="checkbox" name='free' checked={siengeDataConfig.free} onChange={handleSiengeInput} />
                  Free
                </label>
              </div>
            </fieldset>
          )}

          {newCompany.erp === 'PREVISION' && (
            <fieldset>
              <legend>Credenciais Prevision</legend>
              <Field label="Token" full>
                <input type="text" value={previsionToken} onChange={(e) => setPrevisionToken(e.target.value)} />
              </Field>
            </fieldset>
          )}

          <Field label="Api Limits (Rest/Bulk)">
            <select name="api_limits" onChange={handleInput} value={newCompany.api_limits}>
              {API_LIMIT_OPTIONS.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </Field>
          <Field label="Tags (separadas por vírgula)" full>
            <input type="text" name='tagsText' onChange={handleInput} value={newCompany.tagsText} placeholder="ex.: premium, integração" />
          </Field>

          {isEditing && (
            <div className="checkbox-row" style={{ gridColumn: '1 / -1' }}>
              <label>
                <input
                  type="checkbox"
                  name="title_annulments_self_service"
                  checked={newCompany.title_annulments_self_service === true}
                  onChange={handleInput}
                />
                Permitir que a construtora anule títulos no Ampli Obras
              </label>
            </div>
          )}

          <fieldset className="connection-test-fieldset">
            <legend>Conexão com o ERP</legend>
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="button"
                  disabled={testingConnection}
                  onClick={handleTestConnection}
                >
                  {testingConnection ? 'Testando conexão...' : 'Testar Conexão'}
                </button>

                {connectionTest?.supported === false && (
                  <p>Teste de conexão não disponível para esse ERP: {connectionTest.reason}</p>
                )}

                {connectionTest?.supported === true && (
                  <ul>
                    {Object.entries(connectionTest.scopes || {}).map(([key, scope]) => (
                      <li key={key}>
                        {scope.label}: {scope.allowed === true ? 'Liberado' : scope.allowed === false ? 'Negado' : 'Desconhecido'}
                      </li>
                    ))}
                  </ul>
                )}

                {connectionTest && connectionTest.supported === undefined && (
                  <p>{connectionTest.message || 'Não foi possível executar o teste.'}</p>
                )}
              </>
            ) : (
              <p>Disponível após salvar a construtora — a sonda usa a credencial já persistida.</p>
            )}
          </fieldset>

          {isEditing && company.active === false && (
            <p className="inactive-badge">Esta construtora está desativada.</p>
          )}

          {deleteConfirming && (
            <div className="reset-password-confirm">
              <p>Remover definitivamente "{company.name}"? Só funciona se não houver obras vinculadas. Essa ação não pode ser desfeita.</p>
              <button type="button" className="button button-red" onClick={confirmDelete}>Confirmar remoção</button>
              <button type="button" className="button" onClick={() => setDeleteConfirming(false)}>Cancelar</button>
            </div>
          )}
          {actionFeedback && <p className="reset-password-feedback">{actionFeedback}</p>}

          <div className="edit-user-actions">
            <button disabled={newCompany.name === '' || newCompany.subdomain === "" || newCompany.erp === ''} className="button button-accent" onClick={handleSend}>
              {isEditing ? 'Salvar' : 'Enviar'}
            </button>
            {isEditing && (
              <>
                <button type="button" className="button" onClick={toggleActive}>
                  {company.active === false ? 'Ativar' : 'Desativar'}
                </button>
                <button type="button" className="button button-red" onClick={() => setDeleteConfirming(true)}>Remover</button>
              </>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default NewCompany;
